/**
 * CatalogAskWidget — floating "Ask the catalog" chat.
 *
 * Supports the same controlled/standalone modes as AskWidget.
 * Pass `open` + `onClose` to let ChatWidget own the button;
 * omit them for the legacy standalone floating-button mode.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Sparkles, Send, X, Loader2, Package } from "lucide-react";
import { v2Api } from "@/lib/v2Api";
import { Badge } from "@/components/ui/badge";
import { clearPageAsk, registerPageAsk } from "@/lib/pageAskRegistry";

export default function CatalogAskWidget({
  open: openProp,
  onClose,
  onOpen,
  buttonColor = "bg-emerald-600 hover:bg-emerald-700",
}) {
  const navigate = useNavigate();
  const controlled = openProp !== undefined;
  const [ownOpen, setOwnOpen] = useState(false);
  const open = controlled ? openProp : ownOpen;
  const setOpen = controlled
    ? (v) => { if (v) onOpen?.(); else onClose?.(); }
    : setOwnOpen;

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const listRef = useRef(null);

  // Register into ChatWidget column when controlled
  useEffect(() => {
    if (!controlled) return;
    registerPageAsk({ title: "Ask catalog", buttonColor, onOpen: () => onOpen?.() });
    return () => clearPageAsk();
  }, [controlled, buttonColor, onOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const ask = async () => {
    const q = query.trim();
    if (!q || loading) return;
    setLoading(true);
    try {
      const data = await v2Api.catalog.ask(q, 8);
      setHistory((h) => [
        ...h,
        {
          q,
          answer: data.answer || "(no answer)",
          citations: data.citations || [],
          model: data.model,
        },
      ]);
      setQuery("");
      setTimeout(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      }, 50);
    } catch (err) {
      setHistory((h) => [
        ...h,
        {
          q,
          answer:
            err?.response?.data?.detail ||
            err?.message ||
            "Failed to query catalog.",
          citations: [],
          model: "error",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      ask();
    }
  };

  // Standalone mode
  if (!controlled && !open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-30 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 transition-colors"
        title="Ask the catalog"
      >
        <Sparkles className="w-4 h-4" /> Ask catalog
      </button>
    );
  }

  if (!open) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[60] w-[420px] max-w-[92vw] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col"
      style={{ maxHeight: "70vh" }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50 rounded-t-xl">
        <div className="inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-600" />
          <span className="font-semibold text-slate-800 text-sm">Ask the catalog</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-slate-400 hover:text-slate-700"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {history.length === 0 && (
          <div className="text-xs text-slate-500 leading-relaxed">
            <p className="mb-2">Try questions like:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>"Which products carry IECEx or ATEX certification?"</li>
              <li>"List analysers that handle hazardous areas."</li>
              <li>"Which gateways support Modbus RTU?"</li>
            </ul>
          </div>
        )}
        {history.map((h, i) => (
          <div key={i} className="space-y-2">
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-900">
              <MessageCircle className="w-3 h-3 inline mr-1" /> {h.q}
            </div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">
              {h.answer}
            </div>
            {h.citations.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                  Citations
                </p>
                {h.citations.map((c, idx) => (
                  <button
                    key={c.product_id}
                    type="button"
                    onClick={() => navigate(`/v2/catalog/${c.product_id}`)}
                    className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 text-xs"
                  >
                    <span className="font-mono text-slate-400 text-[10px]">#{idx + 1}</span>
                    <Package className="w-3 h-3 text-emerald-500" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{c.name}</div>
                      <div className="text-slate-500 truncate">{c.code}</div>
                    </div>
                    {c.category && (
                      <Badge variant="outline" className="text-[10px] capitalize shrink-0">
                        {c.category}
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            )}
            {h.model && h.model !== "error" && (
              <p className="text-[10px] text-slate-400">model: {h.model}</p>
            )}
          </div>
        ))}
        {loading && (
          <div className="text-xs text-slate-500 inline-flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Searching catalog…
          </div>
        )}
      </div>

      <div className="border-t p-3 flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKey}
          placeholder="Ask about products, certifications, specs…"
          disabled={loading}
          className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-emerald-500"
        />
        <button
          type="button"
          onClick={ask}
          disabled={loading || !query.trim()}
          className="px-3 py-2 rounded-md bg-emerald-600 text-white disabled:bg-slate-300 hover:bg-emerald-700 inline-flex items-center"
          title="Send"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
